import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  FormControlLabel,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import PreviewIcon from '@mui/icons-material/Preview';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import client from 'client';
import { changeCurrency, constants, formatter, PERMISSION_MODEL } from '../../../constants';
import { hasPermission } from '../loadz/utils';
import { showMessage } from 'app/store/fuse/messageSlice';
import ModalGenerateInvoices from 'app/main/Invoices/Invoices/Modals/ModalGenerateInvoices';
import ModalToApplyPayments from 'app/main/Customers/NewCustomersComponents/ModalToApplyPayments';
import { buildOopInvoicesPdfBlob, printOopInvoicesPdf } from './OopInvoicesPrintPdf';
import OopInvoicesEmailModal from './OopInvoicesEmailModal';

const headerCellSx = {
  borderColor: 'black',
  borderBottom: 2,
  backgroundColor: 'white',
  ':hover': { cursor: 'pointer' },
};

function csvFilenameFromResponse(response, fallback) {
  const header = response?.headers?.['content-disposition'] || '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
  const raw = match?.[1] || match?.[2];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch (error) {
    return raw;
  }
}

async function saveBlobWithDialog(blob, filename) {
  if (typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;
    }
  }
  saveAs(blob, filename);
  return true;
}

function parseMoney(value) {
  const number = parseFloat(String(value ?? '').replace(/\$|,/g, ''));
  return Number.isFinite(number) ? number : 0;
}

function moneyText(value) {
  return String(value ?? '').replace('$', '');
}

function groupsFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.job_no_search)) return data.job_no_search;
  if (Array.isArray(data?.invoice_no_search)) return data.invoice_no_search;
  return [];
}

function oopIdentity(source) {
  if (!source || typeof source !== 'object') {
    return { oopName: '', oopCompacctnum: '', oopCompanyId: '' };
  }
  return {
    oopName:
      source.compname ||
      source.ownerop_name ||
      source.oop_name ||
      source.owner_operator_name ||
      source.oopName ||
      '',
    oopCompacctnum:
      source.compacctnum ||
      source.compacctNum ||
      source.oop_compacctnum ||
      source.ownerop_compacctnum ||
      source.compacctNumSub ||
      '',
    oopCompanyId: source.company_id || source.companyId || source.oop_company_id || '',
  };
}

function withOopIdentity(invoice, group) {
  const own = oopIdentity(invoice);
  const parent = oopIdentity(group);
  return {
    ...invoice,
    oopName: own.oopName || parent.oopName,
    oopCompacctnum: own.oopCompacctnum || parent.oopCompacctnum,
    oopCompanyId: own.oopCompanyId || parent.oopCompanyId,
  };
}

function invoicesFromPayload(data) {
  if (!data || data.msg) return [];
  if (Array.isArray(data?.invoices)) {
    return data.invoices.map((invoice) => withOopIdentity(invoice, data));
  }
  const groups = groupsFromResponse(data);
  const invoices = [];
  groups.forEach((group) => {
    if (Array.isArray(group?.invoices) && group.invoices.length) {
      group.invoices.forEach((invoice) => invoices.push(withOopIdentity(invoice, group)));
      return;
    }
    if (group?.invoice != null || group?.invoiceId != null) {
      invoices.push(withOopIdentity(group, null));
    }
  });
  return invoices;
}

function isPaymentPartial(paymentRow) {
  const balance = parseMoney(
    paymentRow?.invoice_balance ?? paymentRow?.payments?.[0]?.invoice_balance
  );
  const total = parseMoney(
    paymentRow?.invoice_total_amount ?? paymentRow?.payments?.[0]?.invoice_total_amount
  );
  return balance > 0 && total > balance;
}

function isInvoicePartiallyPaid(invoice) {
  const total = changeCurrency(invoice?.total);
  const balance = changeCurrency(invoice?.balance);
  const paid = total - balance;
  return balance > 0 && paid > 0.009;
}

function getInvoiceAmountPaid(invoice) {
  const total = changeCurrency(invoice?.total);
  const balance = changeCurrency(invoice?.balance);
  const paid =
    invoice?.discount != null && invoice?.discount !== ''
      ? Number(invoice.discount)
      : total - balance;
  return Number.isFinite(paid) ? Math.max(paid, 0) : 0;
}

function paymentsFromPayload(data) {
  if (!data || data.msg) return [];
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data.payments)
      ? data.payments
      : Array.isArray(data.payment)
        ? data.payment
        : [];
  return list.map((payment) => {
    const lines = Array.isArray(payment?.payments) ? payment.payments : [];
    const summed = lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
    return {
      ...payment,
      lines,
      displayAmount: payment?.amount_received != null ? Number(payment.amount_received) : summed,
      oopName: payment?.compname || payment?.oopName || '',
      oopCompacctnum: payment?.compacctnum || payment?.oopCompacctnum || '',
    };
  });
}

async function fetchJobOopPayments({ compacctnum, jobNo }) {
  const jobSearch = encodeURIComponent(String(jobNo));
  const result = await client.get(`/payments/payments-oop-by-job/${compacctnum}/${jobSearch}`, {
    params: { page: 1 },
    timeout: 0,
  });
  return paymentsFromPayload(result?.data);
}

async function fetchJobOopInvoices({ compacctnum, jobNo, status }) {
  const jobSearch = encodeURIComponent(String(jobNo));
  const invoices = [];
  const seen = new Set();
  for (let page = 1; page <= 8; page += 1) {
    let data;
    try {
      const result = await client.get(
        `/invoices/invoices-oop-by-job/${compacctnum}/${jobSearch}/${status}`,
        { params: { page }, timeout: 0 }
      );
      data = result?.data;
    } catch (error) {
      break;
    }
    if (data?.msg) break;
    const pageInvoices = invoicesFromPayload(data);
    if (!pageInvoices.length) break;
    let added = 0;
    pageInvoices.forEach((invoice) => {
      const id = String(invoice.invoiceId ?? invoice.invoice);
      if (seen.has(id)) return;
      seen.add(id);
      invoices.push(invoice);
      added += 1;
    });
    if (!added) break;
  }
  return invoices;
}

function MoneyCell({ value }) {
  const amount = moneyText(value).trim();
  return <span style={{ whiteSpace: 'nowrap' }}>{amount ? `$${amount}` : ''}</span>;
}

function PaymentRow({ payment, t }) {
  const [expanded, setExpanded] = useState(false);
  const lines = payment.lines || [];
  const source = lines[0]?.payment_source || payment?.payments?.[0]?.payment_source || '';
  const reference = lines[0]?.reference || payment?.payments?.[0]?.reference || 'N/A';
  const confirmation = lines[0]?.confirmation_no || payment?.payments?.[0]?.confirmation_no || 'N/A';
  return (
    <>
      <TableRow>
        <TableCell align='center' size='small' padding='none' sx={{ cursor: 'pointer' }} onClick={() => setExpanded((open) => !open)}>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </TableCell>
        <TableCell align='center' size='small' padding='none'>
          <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>{payment.oopName}</Typography>
        </TableCell>
        <TableCell align='center' size='small' padding='none'>
          <Typography sx={{ fontSize: 14 }}>{lines.length}</Typography>
        </TableCell>
        <TableCell align='center' size='small' padding='none'>
          <div className='flex flex-col items-center gap-4'>
            <span>{source}</span>
            {isPaymentPartial(payment) ? (
              <Chip
                size='small'
                label={t('PARTIAL_PAYMENT', { defaultValue: 'Partial' })}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                }}
              />
            ) : null}
          </div>
        </TableCell>
        <TableCell align='center' size='small' padding='none'>{reference}</TableCell>
        <TableCell align='center' size='small' padding='none'>{confirmation}</TableCell>
        <TableCell align='center' size='small' padding='none'>
          {formatter.format(payment.displayAmount || 0)}
        </TableCell>
        <TableCell align='center' size='small' padding='none'>
          {payment.date_created ? moment(payment.date_created).format('LLL') : ''}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>{t('INVOICE_NO')}</TableCell>
                  <TableCell align='center'>{t('VALUE_OF_PAYMENT')}</TableCell>
                  <TableCell align='center'>{t('REFERENCE')}</TableCell>
                  <TableCell align='center'>{t('PAYMENT_DATE')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={line.id ?? `${line.invoice_no}-${index}`}>
                    <TableCell align='center'>{line.invoice_no}</TableCell>
                    <TableCell align='center'>{formatter.format(Number(line.amount) || 0)}</TableCell>
                    <TableCell align='center'>{line.reference || 'N/A'}</TableCell>
                    <TableCell align='center'>
                      {line.date_created ? moment(line.date_created).format('LLL') : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function JobOopInvoicesDialog({ open, onClose, job, compacctnum, propData }) {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const user = useSelector((state) => state.loadz_auth?.AuthReducer?.user);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [readyInvoices, setReadyInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [printCompany, setPrintCompany] = useState(null);
  const [sort, setSort] = useState({ key: null, order: 'asc' });
  const [tickets, setTickets] = useState([]);
  const [info, setInfo] = useState({});
  const [viewOop, setViewOop] = useState({ name: '', compacctnum: '', companyId: '' });
  const [payOpen, setPayOpen] = useState(false);
  const [dataToPayments, setDataToPayments] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const openModalViewInvoice = useRef(null);
  const setMessage = useRef(null);
  const reloadRef = useRef(null);
  const [printChoiceOpen, setPrintChoiceOpen] = useState(false);
  const [printSections, setPrintSections] = useState({ unpaid: true, ready: true, payments: true });
  const [printing, setPrinting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const jobLabel =
    job?.company_id != null && job?.job_no != null ? `${job.company_id}-${job.job_no}` : job?.job_no;

  const notify = (message, variant) => {
    dispatch(showMessage({ message, variant, autoHideDuration: 4000, anchorOrigin: { vertical: 'top', horizontal: 'right' } }));
  };

  useEffect(() => {
    setMessage.current = notify;
  });

  useEffect(() => {
    if (!open) return undefined;
    const userId = user?.user_id;
    if (!userId) return undefined;
    let cancelled = false;
    const loadCompany = async () => {
      try {
        const result = await client.get(`/company/${userId}`);
        if (!cancelled) setPrintCompany(result?.data || null);
      } catch (error) {
        if (!cancelled) setPrintCompany(null);
      }
    };
    loadCompany();
    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id]);

  const sessionCompany = printCompany || user?.company || null;
  const companyLogoKey = sessionCompany?.logo;
  const companyLogoSrc = companyLogoKey
    ? String(companyLogoKey).includes('http')
      ? companyLogoKey
      : `${constants.URLLOCAL}/aws/signed?image=${companyLogoKey}`
    : '';
  const companyAddress = [
    sessionCompany?.compaddress,
    sessionCompany?.compcity,
    sessionCompany?.compstate,
    sessionCompany?.compzip,
  ]
    .filter((part) => part != null && String(part).trim() !== '')
    .join(', ');

  useEffect(() => {
    if (!open) return undefined;
    const parentComp = compacctnum;
    if (parentComp == null || job?.job_no == null) {
      setUnpaidInvoices([]);
      setReadyInvoices([]);
      setPayments([]);
      return undefined;
    }

    let cancelled = false;
    const jobNo = jobLabel != null && jobLabel !== '' ? jobLabel : job.job_no;

    const load = async () => {
      setLoading(true);
      try {
        const [unpaid, ready, nextPayments] = await Promise.all([
          fetchJobOopInvoices({ compacctnum: parentComp, jobNo, status: 1 }),
          fetchJobOopInvoices({ compacctnum: parentComp, jobNo, status: 0 }),
          fetchJobOopPayments({ compacctnum: parentComp, jobNo }),
        ]);
        if (cancelled) return;
        nextPayments.sort((a, b) => {
          const left = moment(a?.date_created).valueOf() || 0;
          const right = moment(b?.date_created).valueOf() || 0;
          return right - left;
        });
        setUnpaidInvoices(unpaid);
        setReadyInvoices(ready);
        setPayments(nextPayments);
      } catch (error) {
        if (!cancelled) {
          setUnpaidInvoices([]);
          setReadyInvoices([]);
          setPayments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    reloadRef.current = load;
    load();
    return () => {
      cancelled = true;
    };
  }, [open, compacctnum, job?.job_info_id, job?.job_no, job?.company_id, jobLabel]);

  const invoices = useMemo(() => {
    const flat = tabValue === 1 ? readyInvoices : unpaidInvoices;
    if (!sort.key) return flat;
    const direction = sort.order === 'asc' ? 1 : -1;
    const valueOf = (invoice) => {
      if (sort.key === 'invoice') return String(invoice.invoice ?? '');
      if (sort.key === 'driver') return String(invoice.oopName ?? '');
      if (sort.key === 'dateUpdated') return moment(invoice.dateUpdated).valueOf() || 0;
      if (sort.key === 'dueDate') return moment(invoice.dueDate).valueOf() || 0;
      if (sort.key === 'hauling') return parseMoney(invoice.hauling);
      if (sort.key === 'wait') return parseMoney(invoice.wait);
      if (sort.key === 'total') return parseMoney(invoice.total);
      if (sort.key === 'balance') return parseMoney(invoice.balance);
      return '';
    };
    return [...flat].sort((a, b) => {
      const left = valueOf(a);
      const right = valueOf(b);
      if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * direction;
    });
  }, [unpaidInvoices, readyInvoices, tabValue, sort]);

  const hauling = invoices.reduce((sum, invoice) => sum + parseMoney(invoice.hauling), 0);
  const wait = invoices.reduce((sum, invoice) => sum + parseMoney(invoice.wait), 0);
  const total = invoices.reduce((sum, invoice) => sum + parseMoney(invoice.total), 0);
  const balance = invoices.reduce((sum, invoice) => sum + parseMoney(invoice.balance), 0);

  const toggleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, order: current.order === 'asc' ? 'desc' : 'asc' }
        : { key, order: 'asc' }
    );
  };

  const sortMark = (key) => {
    if (sort.key !== key) return null;
    return sort.order === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
    );
  };

  const openView = (invoice) => {
    setViewOop({
      name: invoice.oopName || '',
      compacctnum: invoice.oopCompacctnum,
      companyId: invoice.oopCompanyId,
    });
    setTickets(invoice.ticketsGli || []);
    setInfo({
      id: invoice.invoiceId,
      invoice_id: invoice.invoiceId,
      invoice: invoice.invoice,
      hauling: invoice.hauling,
      material: invoice.material,
      taxesMaterial: invoice.taxesMaterial,
      wait: invoice.wait,
      total: invoice.total,
      balance: invoice.balance,
      dueDate: invoice.dueDate,
      dateCreated: invoice.dateCreated,
      notes: invoice.notes,
    });
    openModalViewInvoice.current?.(false);
  };

  const openPay = async (invoice) => {
    if (!compacctnum || payingId) return;
    setPayingId(invoice.invoiceId);
    setViewOop({
      name: invoice.oopName || '',
      compacctnum: invoice.oopCompacctnum,
      companyId: invoice.oopCompanyId,
    });
    try {
      const result = await client.get(`/invoices/get-apply/subs/${compacctnum}`, {
        headers: { ownerop: true },
      });
      const clients = Array.isArray(result?.data) ? result.data : [];
      const prepared = clients.map((clientRow) => ({
        ...clientRow,
        clientSelected: String(clientRow.compacctNum) === String(invoice.oopCompacctnum),
        selectAll: false,
        payments: (clientRow.payments || []).map((payment) => ({
          ...payment,
          select: String(payment.invoiceId) === String(invoice.invoiceId),
        })),
      }));
      const selected = prepared.find((clientRow) => clientRow.clientSelected);
      const invoiceSelected = selected?.payments?.some((payment) => payment.select);
      if (!selected || !invoiceSelected) {
        notify(t('NO_INVOICES_READY_TO_PAY'), 'warning');
        return;
      }
      setDataToPayments(prepared);
      setPayOpen(true);
    } catch (error) {
      notify(t('NO_INVOICES_READY_TO_PAY'), 'error');
    } finally {
      setPayingId(null);
    }
  };

  const requestOopCsv = async () => {
    const jobId = job?.job_info_id;
    if (!compacctnum || jobId == null || csvLoading) return;
    setCsvLoading(true);
    try {
      const response = await client.post(
        `/invoices/oop/report/${compacctnum}/${jobId}`,
        {},
        { responseType: 'blob' }
      );
      const blob = response?.data;
      if (!blob || response.status === 204 || blob.size === 0) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      if (String(blob.type || '').includes('json')) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      const filename = csvFilenameFromResponse(
        response,
        `oop-invoices-${jobLabel || jobId}.csv`
      );
      setCsvFile({ blob, filename });
      setCsvOpen(true);
    } catch (error) {
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const downloadOopCsv = async () => {
    if (!csvFile?.blob) return;
    const saved = await saveBlobWithDialog(csvFile.blob, csvFile.filename);
    if (saved) {
      setCsvOpen(false);
      setCsvFile(null);
    }
  };

  const invoicePdfProps = {
    companyLogoSrc,
    companyName: sessionCompany?.compname || '',
    companyAddress,
    jobLabel,
    jobNoLabel: t('JOB_NO'),
    projectLabel: t('PROJECT_NAME'),
    projectName: job?.j_destination?.l_name || '-',
    unpaidInvoices,
    readyInvoices,
    payments,
    labels: {
      unpaid: t('UNPAID_INVOICES'),
      ready: t('READY_TO_PAY_INVOICES'),
      payments: t('VIEW_PAYMENTS'),
      invoiceNo: t('INVOICE_NO'),
      oopName: t('OOP_NAME'),
      dueDate: t('DUE_DATE'),
      hauling: t('HAULING'),
      waitTime: t('WAIT_TIME'),
      grandTotal: t('GRAND_TOTAL'),
      balance: t('BALANCE'),
      total: t('TOTAL'),
      empty: t('NO_INVOICES_FOUND'),
      poweredBy: t('POWERED_BY'),
      partial: t('PARTIAL_PAYMENT', { defaultValue: 'Partial' }),
      amountPaid: t('AMOUNT_PAID', { defaultValue: 'Amount Paid' }),
      invoiceCount: t('INVOICE_COUNT'),
      paymentSource: t('PAYMENT_SOURCE'),
      reference: t('REFERENCE'),
      confirmation: t('CONFIRMATION_HASTAG'),
      amountReceived: t('AMOUNT_RECEIVED'),
      paymentDate: t('PAYMENT_DATE'),
      valueOfPayment: t('VALUE_OF_PAYMENT'),
    },
  };

  const canConfirmPrint = printSections.unpaid || printSections.ready || printSections.payments;

  const togglePrintSection = (key) => {
    setPrintSections((current) => ({ ...current, [key]: !current[key] }));
  };

  const confirmPrint = async () => {
    if (!canConfirmPrint || printing) return;
    const previewWindow = window.open('', '_blank');
    setPrintChoiceOpen(false);
    setPrinting(true);
    try {
      await printOopInvoicesPdf({
        previewWindow,
        printSections,
        ...invoicePdfProps,
      });
    } catch (error) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setPrinting(false);
    }
  };

  const canView = hasPermission(PERMISSION_MODEL.ownerOperatorViewInvoice);
  const canPay = hasPermission(PERMISSION_MODEL.ownerOperatorApplyPayment);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
        <DialogTitle sx={{ pr: 26 }}>
          OOP Invoices
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {jobLabel != null && jobLabel !== '' ? `${t('JOB_NO')} ${jobLabel}` : ''}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {t('PROJECT_NAME')}: {job?.j_destination?.l_name || '-'}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              right: 48,
              top: 8,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title={t('PRINT')}>
              <span>
                <IconButton
                  aria-label={t('PRINT')}
                  disabled={loading || printing}
                  onClick={() => setPrintChoiceOpen(true)}
                  sx={{ width: 40, height: 40 }}
                >
                  {printing ? <CircularProgress size={22} /> : <PrintIcon sx={{ fontSize: 28 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('SEND_EMAIL')}>
              <span>
                <IconButton
                  aria-label={t('SEND_EMAIL')}
                  disabled={loading || unpaidInvoices.length + readyInvoices.length + payments.length === 0}
                  onClick={() => setEmailOpen(true)}
                  sx={{ width: 40, height: 40 }}
                >
                  <EmailIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Export CSV'>
              <IconButton
                aria-label='Export CSV'
                disabled={csvLoading}
                onClick={requestOopCsv}
              >
                <svg xmlns='http://www.w3.org/2000/svg' height='32' viewBox='0 -960 960 960' width='32'>
                  <path d='M230-360h120v-60H250v-120h100v-60H230q-17 0-28.5 11.5T190-560v160q0 17 11.5 28.5T230-360Zm156 0h120q17 0 28.5-11.5T546-400v-60q0-17-11.5-31.5T506-506h-60v-34h100v-60H426q-17 0-28.5 11.5T386-560v60q0 17 11.5 30.5T426-456h60v36H386v60Zm264 0h60l70-240h-60l-40 138-40-138h-60l70 240ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z' />
                </svg>
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton aria-label='close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(event, value) => setTabValue(value)}
            sx={{ mb: 2 }}
          >
            <Tab label={t('UNPAID_INVOICES')} />
            <Tab label={t('READY_TO_PAY_INVOICES')} />
            <Tab label={t('VIEW_PAYMENTS')} />
          </Tabs>
          {loading ? <LinearProgress /> : null}
          {tabValue === 2 && !loading ? (
            <TableContainer>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }} />
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('OWNER_OPERATOR_NAME')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('INVOICE_COUNT')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('PAYMENT_SOURCE')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('REFERENCE')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('CONFIRMATION_HASTAG')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('AMOUNT_RECEIVED')}
                    </TableCell>
                    <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                      {t('PAYMENT_DATE')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align='center'>
                        <Typography sx={{ py: 2 }}>{t('NO_INVOICES_FOUND')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <PaymentRow
                        key={`${payment.oopCompacctnum}-${payment.id ?? payment.date_created}`}
                        payment={payment}
                        t={t}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          {tabValue !== 2 && !loading && invoices.length === 0 ? (
            <Typography sx={{ py: 3 }}>{t('NO_INVOICES_FOUND')}</Typography>
          ) : null}
          {tabValue !== 2 && !loading && invoices.length > 0 ? (
            <TableContainer>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('invoice')}>
                      {t('INVOICE_NO')}
                      {sortMark('invoice')}
                    </TableCell>
                    <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('driver')}>
                      {t('OWNER_OPERATOR_NAME')}
                      {sortMark('driver')}
                    </TableCell>
                        <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('dueDate')}>
                          {t('DUE_DATE')}
                          {sortMark('dueDate')}
                        </TableCell>
                        <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('hauling')}>
                          {t('HAULING')}
                          {sortMark('hauling')}
                        </TableCell>
                        <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('wait')}>
                          {t('WAIT_TIME')}
                          {sortMark('wait')}
                        </TableCell>
                        <TableCell align='center' sx={headerCellSx} onClick={() => toggleSort('total')}>
                          {t('GRAND_TOTAL')}
                          {sortMark('total')}
                        </TableCell>
                        <TableCell align='center' sx={{ ...headerCellSx, minWidth: 150 }} onClick={() => toggleSort('balance')}>
                          {t('BALANCE')}
                          {sortMark('balance')}
                        </TableCell>
                        <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                          {t('VIEW')}
                        </TableCell>
                        <TableCell align='center' sx={{ ...headerCellSx, ':hover': undefined }}>
                          {t('PAY')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow
                          key={`${invoice.oopCompacctnum}-${invoice.invoiceId ?? invoice.invoice}`}
                          sx={
                            invoice.missingOOPUnitCostRate
                              ? {
                                  backgroundColor: '#FFE0B2',
                                  '& > .MuiTableCell-root': { backgroundColor: '#FFE0B2' },
                                }
                              : undefined
                          }
                        >
                          <TableCell align='center' size='small' padding='none'>
                            <div className='flex flex-col items-center gap-4'>
                              <div className='flex items-center justify-center gap-4'>
                                <span>{invoice.invoice}</span>
                                {invoice.missingOOPUnitCostRate ? (
                                  <Tooltip title={t('MISSING_OOP_UNIT_COST_RATE_WARNING')} placement='top'>
                                    <WarningIcon sx={{ color: 'red', fontSize: 18, cursor: 'pointer' }} />
                                  </Tooltip>
                                ) : null}
                              </div>
                              {tabValue === 0 && isInvoicePartiallyPaid(invoice) ? (
                                <Tooltip
                                  title={`${t('AMOUNT_PAID', { defaultValue: 'Amount Paid' })}: ${formatter.format(getInvoiceAmountPaid(invoice))}`}
                                  placement='top'
                                >
                                  <Chip
                                    size='small'
                                    label={t('PARTIAL_PAYMENT', { defaultValue: 'Partial' })}
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      backgroundColor: '#fff3cd',
                                      color: '#856404',
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell align='center' size='small' padding='none'>
                            <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>{invoice.oopName}</Typography>
                          </TableCell>
                          <TableCell align='center' size='small' padding='none'>
                            {invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : ''}
                          </TableCell>
                          <TableCell align='right' size='small' padding='none'>
                            <MoneyCell value={invoice.hauling} />
                          </TableCell>
                          <TableCell align='right' size='small' padding='none'>
                            <MoneyCell value={invoice.wait} />
                          </TableCell>
                          <TableCell align='right' size='small' padding='none'>
                            <MoneyCell value={invoice.total} />
                          </TableCell>
                          <TableCell align='right' size='small' padding='none' sx={{ minWidth: 150, px: 1.5 }}>
                            <div className='flex flex-col items-end gap-2'>
                              <MoneyCell value={invoice.balance} />
                              {tabValue === 0 && isInvoicePartiallyPaid(invoice) ? (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: '#856404',
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    textAlign: 'right',
                                  }}
                                >
                                  <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
                                    {t('AMOUNT_PAID', { defaultValue: 'Amount Paid' })}:
                                  </span>
                                  <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
                                    {formatter.format(getInvoiceAmountPaid(invoice))}
                                  </span>
                                </Typography>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell align='center' size='small' padding='none'>
                            {canView ? (
                              <Tooltip title={t('VIEW_INVOICE')} placement='top'>
                                <IconButton onClick={() => openView(invoice)}>
                                  <PreviewIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                          <TableCell align='center' size='small' padding='none'>
                            {canPay ? (
                              <Tooltip title={t('PAY')} placement='top'>
                                <IconButton
                                  disabled={changeCurrency(invoice.balance) === 0 || payingId === invoice.invoiceId}
                                  onClick={() => openPay(invoice)}
                                >
                                  <LocalAtmIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }} align='center'>
                          <Typography color='black' fontSize={14} sx={{ fontWeight: 'bold' }}>
                            {t('TOTAL')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }}>
                          <MoneyCell value={formatter.format(hauling)} />
                        </TableCell>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }}>
                          <MoneyCell value={formatter.format(wait)} />
                        </TableCell>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }}>
                          <MoneyCell value={formatter.format(total)} />
                        </TableCell>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }}>
                          <MoneyCell value={formatter.format(balance)} />
                        </TableCell>
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                        <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                      </TableRow>
                    </TableFooter>
                  </Table>
            </TableContainer>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={printChoiceOpen}
        onClose={() => setPrintChoiceOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{t('PRINT')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={printSections.unpaid}
                  onChange={() => togglePrintSection('unpaid')}
                />
              }
              label={t('UNPAID_INVOICES')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printSections.ready}
                  onChange={() => togglePrintSection('ready')}
                />
              }
              label={t('READY_TO_PAY_INVOICES')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printSections.payments}
                  onChange={() => togglePrintSection('payments')}
                />
              }
              label={t('VIEW_PAYMENTS')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintChoiceOpen(false)}>{t('CANCEL')}</Button>
          <Button variant='contained' onClick={confirmPrint} disabled={!canConfirmPrint || printing}>
            {t('PRINT')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={csvOpen} onClose={() => setCsvOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>{t('EXPORT_CSV')}</DialogTitle>
        <DialogContent>
          <Typography>{csvFile?.filename}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvOpen(false)}>{t('CANCEL')}</Button>
          <Button variant='contained' onClick={downloadOopCsv}>
            {t('DOWNLOAD')}
          </Button>
        </DialogActions>
      </Dialog>
      {emailOpen ? (
        <OopInvoicesEmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          compacctnum={compacctnum}
          jobId={job?.job_info_id}
          filename={`oop-invoices-${jobLabel || job?.job_info_id || 'job'}.pdf`}
          jobLabel={jobLabel}
          projectLabel={t('PROJECT_NAME')}
          projectName={job?.j_destination?.l_name || ''}
          createPdf={(printSections) =>
            buildOopInvoicesPdfBlob({ ...invoicePdfProps, printSections })
          }
          notify={notify}
        />
      ) : null}
      <ModalGenerateInvoices
        prop_data={propData}
        tickets={tickets}
        openModalInvoices={openModalViewInvoice}
        info={info}
        ClientName={viewOop.name}
        compacctNumSub={viewOop.compacctnum}
        compNameData={viewOop.name}
        isOwnerOpsView
      />
      <ModalToApplyPayments
        prop_data={propData}
        open={payOpen}
        setOpen={setPayOpen}
        dataToPayments={dataToPayments}
        setDataToPayments={setDataToPayments}
        customerId={viewOop.companyId}
        rechargedAfterChanges={reloadRef}
        setMessage={setMessage}
        compacctNumSub={viewOop.compacctnum}
        isSubContractor
        labelToSelect='Owners'
      />
    </>
  );
}

export default JobOopInvoicesDialog;
